/**
 * Recursively strip `undefined` leaves from a JSONB payload before the
 * data-layer's `jsonbSchema` (see
 * `packages/domain/src/types/common.types.ts`) validates it. The schema
 * only accepts JSON-native values (`string | number | boolean | null |
 * array | record`); `undefined` is rejected with
 * `ZodError: ... received undefined` at paths like `[ParentRiskId]`.
 *
 * ## Why is undefined arriving at the data-layer?
 *
 * 1. tRPC mutation inputs commonly carry optional fields as `undefined`
 *    when not set — e.g. `UpdateRiskRequest.ParentRiskId?: string | null`
 *    is `undefined` when the requester didn't touch it.
 * 2. `runWithApprovalWorkflow` passes the requester's `actionParams.data`
 *    verbatim into the CR's `RequestedChanges` JSONB field on the wire.
 * 3. SuperJSON preserves `undefined` across the wire (its whole point is
 *    representing non-JSON-native values). So the data-layer
 *    `superjson.parse`s a record whose values include `undefined`.
 * 4. The repository's `jsonbSchema.parse(input.RequestedChanges)` then
 *    throws because `jsonValueSchema`'s union doesn't include
 *    `z.undefined()`.
 *
 * ## Semantic contract — `undefined` vs `null`
 *
 * This helper PRESERVES `null` and only strips `undefined`. The two are
 * load-bearing distinctions in the mutation contract:
 *
 *   * `undefined` → field omitted from the request → "no change". The
 *     merge consumer's `workflow.action` spreads `RequestedChanges` over
 *     the current entity state, so a missing key preserves the existing
 *     value.
 *   * `null` → field explicitly set to null → "unset". The key is in
 *     `RequestedChanges` with value `null`, the spread overwrites the
 *     current entity value, and the column becomes NULL.
 *
 * Dropping undefined preserves the "no change" semantic; keeping null
 * preserves the "unset" semantic. Both flows continue to work.
 *
 * ## Recursion
 *
 * `RequestedChanges` is shallow at the workflow level (top-level update
 * payload), but nested objects appear in fields like
 * `CustomAttributeData` (string-keyed map of values). The helper walks
 * arrays and objects recursively to strip nested `undefined` leaves.
 *
 * Spec reference: RSP-1528 (CR migration). Caught when the real
 * `ApprovalContextLoader` landed and the gate started actually firing.
 */

import { z } from 'zod';

import { jsonbSchema } from './common.types';

/**
 * JSON-native shape: every leaf is a value the data-layer's `jsonbSchema`
 * accepts. This describes what a *valid* payload looks like — it is not
 * `stripUndefinedJsonb`'s return type, which stays `unknown` so invalid
 * values survive to be rejected by the schema rather than coerced.
 */
export type JsonbValue =
  | string
  | number
  | boolean
  | null
  | JsonbValue[]
  | { [key: string]: JsonbValue };

/**
 * Only plain objects (`{}` literals and `Object.create(null)`) are walked.
 * A `Date`, `Map`, `Set`, `RegExp` or class instance is `typeof 'object'`
 * but has no enumerable own entries, so recursing into one would quietly
 * flatten it to `{}` — see the note on the catch-all in
 * `stripUndefinedJsonb`.
 */
const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const proto: unknown = Object.getPrototypeOf(value);

  return proto === Object.prototype || proto === null;
};

/**
 * Recursively drop keys whose values are `undefined` (and undefined
 * array elements). Preserves `null` and every other value as-is.
 *
 * Input type is `unknown` because the helper sits at a wire boundary —
 * callers pass `actionParams.data`, which is typed loosely upstream
 * (workflow declarations type-check the input shape but the data is
 * `Record<string, unknown>` by the time it reaches the helper).
 *
 * ## Strip, don't sanitise
 *
 * Values that are not JSON-native (`Date`, `Map`, `Set`, `bigint`,
 * `symbol`, functions, class instances) pass through UNTOUCHED so
 * `jsonbSchema` still rejects them. This matters because SuperJSON
 * transports several of them natively — a `Date` really can arrive here.
 * An earlier revision coerced them (non-plain objects to `{}`, other
 * primitives to `null`) to keep the return type narrow, which meant
 * `jsonbInputSchema` silently accepted a payload the raw `jsonbSchema`
 * would have rejected and persisted `{}` where the client sent a `Date`.
 * Silent corruption is worse than a 400, so the return type is `unknown`
 * and validation is left to the schema (COI-589 review).
 *
 * Top-level `undefined` returns `null` so the result is always a valid
 * JSONB value (callers usually wrap the input in `?? {}` so the top
 * level is a record). Nested `undefined` is filtered out before the
 * recursion, so it never reaches this branch.
 */
export const stripUndefinedJsonb = (value: unknown): unknown => {
  if (value === undefined) {
    return null;
  }
  if (Array.isArray(value)) {
    return value
      .filter((v): v is Exclude<typeof v, undefined> => v !== undefined)
      .map(stripUndefinedJsonb);
  }
  if (isPlainObject(value)) {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      if (v !== undefined) {
        result[k] = stripUndefinedJsonb(v);
      }
    }

    return result;
  }

  // Primitives (including `null`) and every non-plain object — `jsonbSchema`
  // decides whether each is valid, not this helper.
  return value;
};

/**
 * `jsonbSchema` with the `undefined` leaves stripped first — use this for
 * any tRPC input that accepts a client-supplied JSONB payload.
 *
 * Frontend forms routinely emit optional keys they never populated as
 * `undefined` (react-hook-form registers the field, the user leaves it
 * blank). Legacy Apollo dropped those keys in `JSON.stringify`; SuperJSON
 * preserves them, so the raw `jsonbSchema` rejects the whole payload with
 * a 400 — the class of bug behind RSP-1528 (change-request
 * `RequestedChanges`), the custom_datasource form, and COI-589 (saving an
 * edited dashboard).
 *
 * Compose modifiers on the outside (`jsonbInputSchema.nullish()`) so an
 * absent value short-circuits before the strip runs — that keeps the
 * "omitted = no change" / "null = unset" distinction intact.
 *
 * ## Safe on required fields too (COI-603)
 *
 * On a REQUIRED field there is no modifier to short-circuit, so Zod runs the
 * preprocess with `undefined` for a missing key and `stripUndefinedJsonb`
 * maps that to `null`. That does NOT quietly un-require the field, because
 * `jsonbSchema` is a union of array-or-record and so rejects `null` at the
 * top level — an omitted required blob is still a 400, not a NULL write.
 * The `jsonbSchema`-rejects-`null` behaviour is what makes the swap safe
 * everywhere; there are tests below pinning it, because relaxing it would
 * silently turn every required JSONB field optional.
 */
export const jsonbInputSchema = z.preprocess(stripUndefinedJsonb, jsonbSchema);
